import { IAddressElement } from '../../components/accounts/modal/paymentAndBilling/address';
import { AnyAction, createSlice } from '@reduxjs/toolkit';
import {
  fetchAccountById
} from './accounts.thunks';
import { ApiError } from '../../services/api';
import { CadenceTypesValues } from '../../components/auth/proRegister/subscriptionTypes';
import { AccessTypes as AccountAccessTypes } from '../../components/common/domain/accountAccessType';
import { ICurrentDeal } from './currentRenewal/currentRenewal.thunk';
import { IPackageInfo } from '../packagesInfo';

export enum AccessType {
  Trial = 'trial',
  FullAccess = 'fullAccess',
  Entry = 'entry'
}

export enum PermissionAccessTypes {
  Hidden = 'HIDDEN',
  Available = 'AVAILABLE',
  Disabled = 'DISABLED',
}

export interface ManagerDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface ApiCredentials {
  rsaKey: string;
  recipientsEmails: string[];
  filename: string;
  pkcs8?: string;
  isActive?: boolean;
  clientId: string;
  createdAt?: Date;
  updatedAt?: Date;
  kmsKeyId?: string;
}

export interface AccountFeature {
  id: string;
  name: string;
  access?: PermissionAccessTypes;
}

export interface AccountFeature {
  id: string;
  name: string;
  access?: PermissionAccessTypes;
}

export interface AccountPermission {
  name: string;
  accessLevel: PermissionAccessTypes;
}

export interface IdentityProvider {
  id: string;
  name: string;
  type: string;
  metadataUrl: string;
  metadataFile: string;
  clientId: string;
  clientSecret: string;
  authorizeScopes: string;
  attributeRequestMethod: string;
  issuer: string;
  authorizeUrl?: string;
  tokenUrl?: string;
  attributesUrl?: string;
  jwksUri?: string;
}

export interface IIdentifier {
  id: string;
}

interface ISubscriptionInfo {
  cycle: CadenceTypesValues;
  cycleEndDate: number;
  cycleStartDate: number;
  lastPaymentDate: number;
  startDate: number;
  isPaymentMissing: boolean;
  cancellationInProgress: boolean;
  usersToDisableAtEndOfCycle: string[];
  seatsToRemoveAtEndOfCycle?: number;
}

export interface IAccount {
  id: string;
  name: string;
  accessType: AccountAccessTypes;
  address?: string;
  logo?: string;
  renewalAt?: Date;
  seatsUsed: number;
  seatsAvailable?: number;
  seatsPurchased: number;
  createdAt?: Date;
  updatedAt?: Date;
  isActive: boolean; // remove when removing plgexternal feature toggle
  billingContact?: string;
  responsesAllowed?: number;
  trialDays?: number;
  managerDetails?: ManagerDetails;
  customerSuccessManagerDetails?: ManagerDetails;
  lastCampaignLaunchAt?: Date;
  apiCredentialsId?: string;
  apiCredentials?: ApiCredentials;
  allowAccessUntil?: Date;
  upgradedAt?: Date;
  policyId?: string;
  policyDetails?: AccountFeature[];
  upgradeInfo?: UpgradeInfo;
  forceSecondFactor?: boolean;
  hubspotId?: string;
  identityProviderId?: string;
  identityProvider?: IdentityProvider;
  useSsoOnly: boolean;
  paymentOptions: Array<string>;
  sampleDiscountPercentage: number;
  billingAddress: IAddressElement | null;
  region?: string;
  status?: AccountStates;
  priceOfNewLicenses?: number;
  invoiceTerms?: string;
  renewalDate?: string;
  permissions: AccountPermission[];
  subscription: ISubscriptionInfo;
  responsesAllowedBeforeLapsed?: number;
  lastLoginDate?: Date;
  currentDeal: ICurrentDeal | null | undefined;
  contractRenewalDate?: string;
  packagesInfo?: {
    current: IPackageInfo;
    past: IPackageInfo;
  };
  questionsPackages?: string[];
}

export interface AccountsFilter {
  id: string;
  name: string;
  accessType: string;
  renewalAt: string;
  seatsUsed: number;
  seatsPurchased: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean; // remove when removing plgexternal feature toggle
}

export const accountSorter = (a: IAccount, b: IAccount): number => {
  return a.name.toLocaleLowerCase().localeCompare(b.name);
};

export interface IHubspotAccount {
  hubspotId: string;
  sightxId?: string;
  name: string;
}

export interface UpgradeInfo {
  startedAt?: Date;
  updatedAt?: Date;
  status?: UpgradeStatus;
}

export enum UpgradeStatus {
  Success = 'SUCCESS',
  SuccessWithErrors = 'SUCCESS_WITH_ERRORS',
  Error = 'ERROR',
  InProgress = 'IN_PROGRESS',
}

export interface IFormPermission {
  name: string;
  userFriendlyName: string;
  description: string;
  checked: boolean;
  label?: string;
}

export enum ActiveLapsedFilterState {
  Active = 'ACTIVE',
  Lapsed = 'LAPSED',
}

export enum AccountStates {
  ACTIVE = 'active',
  LAPSED = 'lapsed',
  DISABLED = 'disabled',
}

/** @deprecated use the one on src/components/common/domain/accountAccessType.ts */
export enum AccessTypes {
  Lite = 'LITE',
  Pro = 'PRO',
  Enterprise = 'ENTERPRISE'
}

export interface IAccountState {
  error: ApiError | null;
  loading: boolean;
  account: IAccount | null;
}

const initialState: IAccountState = {
  error: null,
  loading: false,
  account: null
};

export const extraReducers = {
  [fetchAccountById.pending.type]: (state: IAccountState): void => {
    state.loading = true;
    state.account = null;
  },
  [fetchAccountById.fulfilled.type]: (state: IAccountState, action: AnyAction): void => {
    state.loading = false;
    state.account = action.payload;
  },
  [fetchAccountById.rejected.type]: (state: IAccountState, action: AnyAction): void => {
    state.loading = false;
    state.error = action.payload;
  }
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {},
  extraReducers
});

export default accountSlice.reducer;
export const actions = {
  ...accountSlice.actions,
  fetchAccountById
};

